import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Collapse, Typography, useMediaQuery } from "@mui/material";
import { Images } from "../../utils/Images";
import dotWhite from "../../assets/Images/TempleDetail/dot-white.svg";
import dotGrey from "../../assets/Images/TempleDetail/dot-grey.svg";
import arrowRightRed from "../../assets/Images/TempleDetail/arrow-right-red.svg";
import chevronRight from "../../assets/Images/TempleDetail/chevron-right.svg";
import { T, FONT, SectionCard, SectionHeading, Photo } from "./shared";
import { MobileHeading } from "./mobile/shared";

// Updates come with HTML descriptions from admin — show them as plain paragraphs.
const toText = (html) => {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = String(html).replace(/<\/(p|div|li|h\d)>/gi, "\n").replace(/<br\s*\/?>/gi, "\n");
  return (el.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};
const fmtDate = (d, opts) => {
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB", opts);
};

const AUTO_MS = 4000;

// YouTube on updates (admin → Updates → video link), shown like the campaign page's Updates:
// the player takes the photo's place. Admin saves embed links; watch / youtu.be / shorts links work too.
const youTubeId = (url) => {
  const m = String(url || "").match(/(?:youtube\.com\/(?:embed\/|watch\?(?:.*&)?v=|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
};
const embedUrl = (url) => {
  const id = youTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}` : url || "";
};

// Video player in the media slot (replaces the photos when the update has a video)
const UpdateVideo = ({ url, latest, height, title }) => (
  <Box sx={{ position: "relative", width: "100%", height, overflow: "hidden", background: "#000" }}>
    <Box
      component="iframe"
      src={embedUrl(url)}
      title={title || "Update video"}
      frameBorder="0"
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
      sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, display: "block" }}
    />
    {latest && (
      <Box sx={{ position: "absolute", right: "12px", top: "52px", display: "flex", alignItems: "center", gap: "6px", pl: "10px", pr: "12px", py: "6px", borderRadius: "100px", background: T.red, pointerEvents: "none" }}>
        <Box component="img" src={dotWhite} alt="" sx={{ width: "6px", height: "6px" }} />
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "11px", lineHeight: "15px", color: "#FFFFFF" }}>Latest update</Typography>
      </Box>
    )}
  </Box>
);

// Spotlight photo with dots (Figma "Update Photo Carousel"). Several photos slide on their own every
// 4s and can be swiped; a swipe or dot tap holds the auto-advance for one round.
const Photos = ({ images, latest, height }) => {
  const [i, setI] = useState(0);
  const list = images.length ? images : [Images.fallBackImageCard];
  const count = list.length;
  const touchX = useRef(null);
  const heldUntil = useRef(0);
  const goTo = (n) => {
    heldUntil.current = Date.now() + AUTO_MS;
    setI(((n % count) + count) % count);
  };
  useEffect(() => {
    if (count < 2) return undefined;
    const id = setInterval(() => {
      if (Date.now() < heldUntil.current || document.hidden) return;
      setI((cur) => (cur + 1) % count);
    }, AUTO_MS);
    return () => clearInterval(id);
  }, [count]);
  return (
    <Box
      sx={{ position: "relative", width: "100%", height, overflow: "hidden", background: T.maroon, touchAction: "pan-y" }}
      onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchX.current == null || count < 2) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 35) goTo(i + (dx < 0 ? 1 : -1));
      }}
    >
      <Box sx={{ position: "absolute", inset: 0, display: "flex", transform: `translateX(-${i * 100}%)`, transition: "transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1)" }}>
        {list.map((src, n) => (
          <Photo key={n} src={src} loading="lazy" sx={{ flex: "0 0 100%", width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ))}
      </Box>
      {latest && (
        <Box sx={{ position: "absolute", left: "14px", top: "14px", display: "flex", alignItems: "center", gap: "6px", pl: "10px", pr: "12px", py: "6px", borderRadius: "100px", background: T.red }}>
          <Box component="img" src={dotWhite} alt="" sx={{ width: "6px", height: "6px" }} />
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "11px", lineHeight: "15px", color: "#FFFFFF" }}>Latest update</Typography>
        </Box>
      )}
      {list.length > 1 && (
        <Box sx={{ position: "absolute", left: "50%", transform: "translateX(-50%)", bottom: "14px", display: "flex", gap: "6px", alignItems: "center", px: "10px", py: "7px", borderRadius: "100px", background: "rgba(26,8,8,0.45)", backdropFilter: "blur(5px)" }}>
          {list.map((_, n) => (
            <Box
              key={n}
              component="button"
              type="button"
              aria-label={`Photo ${n + 1}`}
              onClick={(e) => {
                e.stopPropagation();
                goTo(n);
              }}
              sx={{ width: n === i ? "18px" : "6px", height: "6px", p: 0, border: "none", borderRadius: "3px", background: n === i ? "#FFFFFF" : "rgba(255,255,255,0.5)", cursor: "pointer", transition: "width 0.2s" }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Figma 3478:6064 "Updates — Latest Spotlight": newest update as a card, older ones as rows.
// Updates open in place like an accordion: one open at a time, opening another closes it.
const TempleUpdates = ({ updates = [] }) => {
  const isMobile = useMediaQuery("(max-width:760px)");
  // Phones follow Figma "Mobile revamp" 739:17529
  const phone = useMediaQuery("(max-width:600px)");
  // key of the one open update ("latest" or an earlier update's key); null = all closed
  const [openKey, setOpenKey] = useState(null);
  const toggle = (key) => setOpenKey((cur) => (cur === key ? null : key));

  const list = useMemo(
    () =>
      [...updates]
        .map((u, idx) => ({ ...u, text: toText(u.description) }))
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .map((u, idx, arr) => ({ ...u, number: arr.length - idx })),
    [updates]
  );
  if (!list.length) return null;
  const [latest, ...earlier] = list;
  const titleOf = (u) => u.title || u.text.split("\n")[0].slice(0, 80) || `Update #${u.number}`;
  const keyOf = (u) => String(u.id ?? u.number);
  const latestOpen = openKey === "latest";

  return (
    <SectionCard id="updates" sx={{ display: "flex", flexDirection: "column", gap: phone ? "20px" : "24px", ...(phone && { borderRadius: "20px", boxShadow: "0px 8px 24px 0px rgba(89,38,13,0.06)" }) }}>
      {phone ? (
        <MobileHeading eyebrow="Updates" hindi="॥ समाचार ॥" title="Campaign updates" />
      ) : (
        <SectionHeading eyebrow="Updates" hindi="॥ समाचार ॥" title="Campaign updates" />
      )}

      {/* Spotlight — side by side while collapsed; opened, the photo goes on top at a fixed
          height and the full text runs underneath, so long text never stretches the photo */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile || latestOpen ? "column" : "row",
          border: "0.5px solid rgba(201,149,43,0.4)",
          borderRadius: phone ? "16px" : "20px",
          overflow: "hidden",
          minHeight: isMobile || latestOpen ? "auto" : "250px",
        }}
      >
        <Box sx={{ width: isMobile || latestOpen ? "100%" : "300px", flexShrink: 0, display: "flex" }}>
          {latest.video_url ? (
            <UpdateVideo url={latest.video_url} latest title={titleOf(latest)} height={isMobile ? "220px" : latestOpen ? "340px" : "100%"} />
          ) : (
            <Photos images={latest.images || []} latest height={isMobile ? "220px" : latestOpen ? "340px" : "100%"} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, px: phone ? "16px" : isMobile ? "20px" : "28px", py: phone ? "16px" : isMobile ? "20px" : "26px", display: "flex", flexDirection: "column", justifyContent: "center", gap: phone ? "10px" : "12px" }}>
          {phone ? (
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "10px", lineHeight: 1.5, color: T.red, whiteSpace: "pre-wrap" }}>
              {`UPDATE #${latest.number}  ·  ${fmtDate(latest.date, { day: "2-digit", month: "short", year: "numeric" })}`}
            </Typography>
          ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "11px", lineHeight: "14px", color: T.red }}>UPDATE #{latest.number}</Typography>
            <Box component="img" src={dotGrey} alt="" sx={{ width: "3px", height: "3px" }} />
            <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: "12px", lineHeight: "16px", color: T.muted }}>
              {fmtDate(latest.date, { day: "2-digit", month: "short", year: "numeric" })}
            </Typography>
          </Box>
          )}
          <Typography component="h3" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: phone ? "20px" : isMobile ? "19px" : "22px", lineHeight: phone ? 1.5 : "28px", color: T.ink }}>{titleOf(latest)}</Typography>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: phone ? 1.5 : "22px",
              color: phone ? "#4A4542" : T.bodyWarm,
              whiteSpace: "pre-line",
              maxWidth: "75ch",
              ...(!latestOpen && { display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }),
            }}
          >
            {latest.text}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-expanded={latestOpen}
            onClick={() => toggle("latest")}
            sx={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: phone ? "6px" : "8px", p: 0, border: "none", background: "transparent", cursor: "pointer", fontFamily: FONT, fontWeight: 700, fontSize: phone ? "13px" : "14px", lineHeight: phone ? "normal" : "20px", color: T.red, "&:hover": { textDecoration: "underline" } }}
          >
            {latestOpen ? "Show less" : "Read full update"}
            <Box component="img" src={arrowRightRed} alt="" sx={{ width: "15px", height: "15px", transform: latestOpen ? "rotate(-90deg)" : "rotate(90deg)", transition: "transform 0.2s" }} />
          </Box>
        </Box>
      </Box>

      {/* Earlier updates */}
      {earlier.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: phone ? "10px" : "11px", lineHeight: phone ? 1.5 : "14px", letterSpacing: phone ? 0 : "1.32px", color: T.muted }}>EARLIER</Typography>
          {earlier.map((u) => {
            const isOpen = openKey === keyOf(u);
            return (
            <Box key={keyOf(u)} sx={{ borderBottom: `1px solid ${T.sand}` }}>
            <Box
              component="button"
              type="button"
              aria-expanded={isOpen}
              onClick={() => toggle(keyOf(u))}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: phone ? "10px" : "16px",
                py: "14px",
                px: 0,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
                "&:hover .row-title": { color: T.red },
                "&:focus-visible": { outline: `2px solid ${T.gold}`, outlineOffset: "2px" },
              }}
            >
              <Box sx={{ width: phone ? "40px" : "48px", height: phone ? "44px" : "48px", flexShrink: 0, borderRadius: phone ? "10px" : "12px", border: `0.5px solid ${T.red}`, background: "rgba(242,178,186,0.1)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: phone ? "16px" : "17px", lineHeight: phone ? "normal" : "20px", color: phone ? "#000000" : T.ink }}>{fmtDate(u.date, { day: "2-digit" })}</Typography>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: phone ? "8px" : "9px", lineHeight: phone ? "normal" : "12px", color: T.red, textTransform: "uppercase" }}>{fmtDate(u.date, { month: "short" }).slice(0, 3)}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: phone ? "9px" : "10px", lineHeight: phone ? 1.5 : "13px", color: T.muted }}>UPDATE #{u.number}</Typography>
                <Typography
                  className="row-title"
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 700,
                    fontSize: phone ? "12px" : "15px",
                    lineHeight: phone ? 1.5 : "20px",
                    color: isOpen ? T.red : T.ink,
                    overflow: "hidden",
                    transition: "color 0.15s",
                    ...(phone ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" } : { textOverflow: "ellipsis", whiteSpace: "nowrap" }),
                  }}
                >
                  {titleOf(u)}
                </Typography>
              </Box>
              {(youTubeId(u.video_url) || u.images?.[0]) && !isOpen && (
                <Box sx={{ position: "relative", flexShrink: 0, display: phone ? "block" : { xs: "none", sm: "block" } }}>
                  <Photo
                    src={youTubeId(u.video_url) ? `https://img.youtube.com/vi/${youTubeId(u.video_url)}/mqdefault.jpg` : u.images[0]}
                    loading="lazy"
                    sx={phone ? { width: "40px", height: "38px", borderRadius: "7px", objectFit: "cover", display: "block" } : { width: "56px", height: "42px", borderRadius: "8px", objectFit: "cover", display: "block" }}
                  />
                  {youTubeId(u.video_url) && (
                    <Box aria-hidden="true" sx={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: phone ? "16px" : "20px", height: phone ? "11px" : "14px", borderRadius: "3px", background: "#FF0000", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Box sx={{ width: 0, height: 0, borderTop: "3.5px solid transparent", borderBottom: "3.5px solid transparent", borderLeft: "5px solid #FFFFFF", ml: "1px" }} />
                    </Box>
                  )}
                </Box>
              )}
              <Box component="img" src={chevronRight} alt="" sx={{ width: phone ? "12px" : "16px", height: phone ? "12px" : "16px", flexShrink: 0, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
            </Box>
            <Collapse in={isOpen} timeout={250} unmountOnExit>
              <Box sx={{ pb: "16px", pl: phone ? "50px" : "64px", display: "flex", flexDirection: "column", gap: "12px" }}>
                {u.video_url ? (
                  <Box sx={{ borderRadius: "14px", overflow: "hidden" }}>
                    <UpdateVideo url={u.video_url} title={titleOf(u)} height={phone ? "190px" : "300px"} />
                  </Box>
                ) : u.images?.length > 0 && (
                  <Box sx={{ borderRadius: "14px", overflow: "hidden" }}>
                    <Photos images={u.images} height={phone ? "180px" : "260px"} />
                  </Box>
                )}
                <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: "12px", lineHeight: "16px", color: T.muted }}>
                  {fmtDate(u.date, { day: "2-digit", month: "short", year: "numeric" })}
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: phone ? "13px" : "14px", lineHeight: phone ? 1.5 : "22px", color: phone ? "#4A4542" : T.bodyWarm, whiteSpace: "pre-line" }}>{u.text}</Typography>
              </Box>
            </Collapse>
            </Box>
            );
          })}
        </Box>
      )}

    </SectionCard>
  );
};

export default TempleUpdates;
